import {
  EPTSessionType,
  EPTTimestampType,
} from '@tableau/microview-communication-service/lib/src/channelDefinitions/SearchEPTChannelDefinitions';
import { Effect, all, call, put, select, fork } from 'redux-saga/effects';
import { PerformSearchAction } from '../actions/ActionTypes';
import { performSearchFailed } from '../actions/PerformSearchFailedAction';
import { performSearchSucceeded } from '../actions/PerformSearchSucceededAction';
import { performSearchSucceededWithMetadata } from '../actions/PerformSearchSucceededWithMetadataAction';
import { ContentDefinitions } from '../models/contentDefinitions';
import { ContentType } from '../models/ContentType';
import { SearchResultList, SearchResultItem, MixedContentTypeSearchResult } from '../models/SearchResultTypes';
import { selectFilterQuery } from '../selectors/selectSingleContentTypeSearchResult';
import { ContentService } from '../services/ContentService';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';
import { SearchEPTService } from '../services/SearchEPTService';
import { FilterQuery } from '../state/FilterState';

export const INITIAL_PAGESIZE_SLICE: number = 20;

export function* performSearch(action: PerformSearchAction) {
  const callsForCRASS: Effect[] = [];

  const searchEPT = FeatureFlagService.instance.getValue(FeatureFlag.searchEPT);
  if (searchEPT) {
    SearchEPTService.instance.mark(EPTSessionType.fullSearch, action.searchTerm, EPTTimestampType.initialUIRendered);
    SearchEPTService.instance.mark(EPTSessionType.fullSearch, action.searchTerm, EPTTimestampType.sendingRequestsToServerSide);
  }
  for (const contentType in ContentDefinitions) {
    const currentContentType = contentType as ContentType;
    const filters: FilterQuery = yield select(selectFilterQuery);
    //perform search is used for the mixed content page, which should rely on the default sort of relevancy so no sort option is needed
    callsForCRASS.push(
      call(ContentService.instance.fetchContent, currentContentType, action.searchTerm, 0, INITIAL_PAGESIZE_SLICE, filters)
    );
  }

  const resultLists: SearchResultList<SearchResultItem>[] = yield all(callsForCRASS);
  let errorCount = 0;
  const callCount = resultLists.length;
  const mixedContentTypeResult: MixedContentTypeSearchResult = {
    map: resultLists.reduce<{ [type in ContentType]: SearchResultList<SearchResultItem> }>((p, c) => {
      p[c.type] = c;
      errorCount += c.error ? 1 : 0;
      return p;
    }, {} as { [type in ContentType]: SearchResultList<SearchResultItem> }),
  };
  // if all crass calls fail then we dispath a failed search so that the oops page shows
  if (callCount === errorCount && resultLists[0].error) {
    yield put(performSearchFailed(resultLists[0].error));
  } else {
    if (searchEPT) {
      SearchEPTService.instance.mark(EPTSessionType.fullSearch, action.searchTerm, EPTTimestampType.gotResponseFromServerSide);
    }
    yield put(performSearchSucceeded(mixedContentTypeResult));
  }

  for (const contentType in ContentDefinitions) {
    const currentContentType = contentType as ContentType;
    if (mixedContentTypeResult.map[currentContentType].list.length > 0) {
      yield fork(fetchContentMetadata, currentContentType, mixedContentTypeResult.map[currentContentType].list);
    }
  }
}

export function* performSearchV2(action: PerformSearchAction) {
  const searchEPT = FeatureFlagService.instance.getValue(FeatureFlag.searchEPT);
  if (searchEPT) {
    SearchEPTService.instance.mark(EPTSessionType.fullSearch, action.searchTerm, EPTTimestampType.initialUIRendered);
    SearchEPTService.instance.mark(EPTSessionType.fullSearch, action.searchTerm, EPTTimestampType.sendingRequestsToServerSide);
  }

  const filters: FilterQuery = yield select(selectFilterQuery);

  const mixedContentTypeResult: MixedContentTypeSearchResult = yield call(
    ContentService.instance.fetchAllContent,
    action.searchTerm,
    0,
    INITIAL_PAGESIZE_SLICE,
    filters
  );

  // check if mixedContentTypeResult has an error (if so, all crass calls failed)
  // dispath a failed search so that the oops page shows
  if (mixedContentTypeResult.error) {
    yield put(performSearchFailed(mixedContentTypeResult.error));
  } else {
    if (searchEPT) {
      SearchEPTService.instance.mark(EPTSessionType.fullSearch, action.searchTerm, EPTTimestampType.gotResponseFromServerSide);
    }
    yield put(performSearchSucceeded(mixedContentTypeResult));
  }

  for (const contentType in ContentDefinitions) {
    const currentContentType = contentType as ContentType;
    if (mixedContentTypeResult.map[currentContentType].list.length > 0) {
      yield fork(fetchContentMetadata, currentContentType, mixedContentTypeResult.map[currentContentType].list);
    }
  }
}

function* fetchContentMetadata(contentType: ContentType, searchResultItems: SearchResultItem[]) {
  const metadataResult: { contentType: ContentType; data: SearchResultItem[] } | null = yield call(
    ContentService.instance.fetchContentMetadata,
    contentType,
    searchResultItems
  );
  if (metadataResult) {
    yield put(performSearchSucceededWithMetadata(contentType, metadataResult.data));
  }
}
